import { GridAlignment, GridColDef, GridValueGetterParams } from "@mui/x-data-grid-premium";

import {
  BusinessUnitToggle,
  ChannelDataWithSourceAndTotal,
  GeoViewToggle,
  MetricType,
  ViewToggle,
} from "@/shared/types";
import { formatProductHierarchy } from "@/Utils/formatProductHierarchy";
import { formatCurrencyValue, formatPercentValue, formatUnitValue } from "@/Utils/formatters";
import { Layer } from "@/Utils/hierarchy";
import { FormatCurrencyOptions } from "@/hooks/useConfig/types";
import { Config, NO_SYMBOL } from "@/hooks/useConfig";

export const getColumnDefinitions = (
  activeColumns: string[],
  options: {
    currencySymbol: string;
    formatCurrency: (val: number, options?: FormatCurrencyOptions) => string;
    geoView: GeoViewToggle;
    gridView: ViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    metricType: MetricType;
    config: Config;
  }
) => {
  const {
    formatCurrency,
    geoView,
    gridView,
    currencySymbol,
    businessUnitToggle,
    metricType,
    config: { revenueLabel, showReturnInsteadOfRevenue },
  } = options;
  const revenueFormatterOptions = showReturnInsteadOfRevenue ? NO_SYMBOL : {};
  const revenueSuffix = showReturnInsteadOfRevenue ? "" : ` (${currencySymbol})`;
  const channelHeaderName = gridView === ViewToggle.INSIGHT ? "Formats" : "Channels";

  const columns: GridColDef<ChannelDataWithSourceAndTotal>[] = [
    // Channel name (always visible)
    {
      field: "channel",
      headerName: channelHeaderName,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.mappedName,
      sortable: true,
      hideable: !activeColumns.includes("channel"),
      type: "string",
    },

    // Business Unit fields (only when businessUnitToggle is split)
    ...(businessUnitToggle === BusinessUnitToggle.SPLIT
      ? [
          {
            field: "product_hierarchy",
            headerName: "Products",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) => {
              const { brand, category, product } = row;
              return formatProductHierarchy(brand, category, product);
            },
            sortable: true,
            hideable: !activeColumns.includes("product_hierarchy"),
            flex: 1,
          } as GridColDef<ChannelDataWithSourceAndTotal>,
        ]
      : []),

    // Geo fields
    ...(geoView === GeoViewToggle.SPLIT
      ? [
          {
            field: "geography",
            headerName: "Geography",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row.geography,
            hideable: !activeColumns.includes("geography"),
          },
        ]
      : []),

    // Spend fields
    {
      field: "brand_spend",
      headerName: `Brand Spend (${currencySymbol})`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.spend[Layer.Brand],
      valueFormatter: ({ value }: { value: number }) => formatCurrencyValue(value, formatCurrency),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("brand_spend"),
    },
    {
      field: "category_spend",
      headerName: `Category Spend (${currencySymbol})`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.spend[Layer.Category],
      valueFormatter: ({ value }: { value: number }) => formatCurrencyValue(value, formatCurrency),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("category_spend"),
    },
    {
      field: "product_spend",
      headerName: `Product Spend (${currencySymbol})`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.spend[Layer.Product],
      valueFormatter: ({ value }: { value: number }) => formatCurrencyValue(value, formatCurrency),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("product_spend"),
    },
    {
      field: "total_spend",
      headerName: `Total Spend (${currencySymbol})`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.spend.total,
      valueFormatter: ({ value }: { value: number }) => formatCurrencyValue(value, formatCurrency),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("total_spend"),
    },

    // Revenue fields
    {
      field: "brand_revenue",
      headerName: `Brand ${revenueLabel}${revenueSuffix}`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.revenue[Layer.Brand],
      valueFormatter: ({ value }: { value: number }) =>
        formatCurrencyValue(value, formatCurrency, revenueFormatterOptions),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("brand_revenue"),
    },
    {
      field: "category_revenue",
      headerName: `Category ${revenueLabel}${revenueSuffix}`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.revenue[Layer.Category],
      valueFormatter: ({ value }: { value: number }) =>
        formatCurrencyValue(value, formatCurrency, revenueFormatterOptions),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("category_revenue"),
    },
    {
      field: "product_revenue",
      headerName: `Product ${revenueLabel}${revenueSuffix}`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.revenue[Layer.Product],
      valueFormatter: ({ value }: { value: number }) =>
        formatCurrencyValue(value, formatCurrency, revenueFormatterOptions),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("product_revenue"),
    },
    {
      field: "total_revenue",
      headerName: `Total ${revenueLabel}${revenueSuffix}`,
      valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
        row.revenue.total,
      valueFormatter: ({ value }: { value: number }) =>
        formatCurrencyValue(value, formatCurrency, revenueFormatterOptions),
      sortable: true,
      type: "number",
      align: "left" as GridAlignment,
      headerAlign: "left" as GridAlignment,
      hideable: !activeColumns.includes("total_revenue"),
    },

    // ROI fields (shown only when metricType is MARKETING_ROI)
    ...(metricType === MetricType.MARKETING_ROI
      ? [
          {
            field: "brand_roi",
            headerName: "Brand ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roi?.brand !== null ? row?.roi?.brand : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("brand_roi"),
          },
          {
            field: "category_roi",
            headerName: "Category ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roi?.category !== null ? row?.roi?.category : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("category_roi"),
          },
          {
            field: "product_roi",
            headerName: "Product ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roi?.product !== null ? row?.roi?.product : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("product_roi"),
          },
          {
            field: "total_roi",
            headerName: "Total ROI (%)",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roi?.total !== null ? row?.roi?.total : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("total_roi"),
          },
        ]
      : []),

    // ROAS fields (shown only when metricType is RETURN_ON_AD_SPEND)
    ...(metricType === MetricType.RETURN_ON_AD_SPEND
      ? [
          {
            field: "brand_roas",
            headerName: "Brand ROAS",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roas?.brand !== null ? row?.roas?.brand : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("brand_roas"),
          },
          {
            field: "category_roas",
            headerName: "Category ROAS",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roas?.category !== null ? row?.roas?.category : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("category_roas"),
          },
          {
            field: "product_roas",
            headerName: "Product ROAS",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roas?.product !== null ? row?.roas?.product : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("product_roas"),
          },
          {
            field: "total_roas",
            headerName: "Total ROAS",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.roas?.total !== null ? row?.roas?.total : 0,
            valueFormatter: ({ value }: { value: number }) => formatPercentValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("total_roas"),
          },
        ]
      : []),

    // Units fields (shown only when metricType is SALES_UNITS)
    ...(metricType === MetricType.SALES_UNITS
      ? [
          {
            field: "brand_units",
            headerName: "Brand Units",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.units?.brand !== null ? row?.units?.brand : 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("brand_units"),
          },
          {
            field: "category_units",
            headerName: "Category Units",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.units?.category !== null ? row?.units?.category : 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("category_units"),
          },
          {
            field: "product_units",
            headerName: "Product Units",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.units?.product !== null ? row?.units?.product : 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("product_units"),
          },
          {
            field: "total_units",
            headerName: "Total Units",
            valueGetter: ({ row }: GridValueGetterParams<ChannelDataWithSourceAndTotal>) =>
              row?.units?.total !== null ? row?.units?.total : 0,
            valueFormatter: ({ value }: { value: number }) => formatUnitValue(value),
            sortable: true,
            type: "number",
            align: "left" as GridAlignment,
            headerAlign: "left" as GridAlignment,
            hideable: !activeColumns.includes("total_units"),
          },
        ]
      : []),
  ];

  return columns;
};
